import { useState, useEffect, useCallback } from 'react'
import { listClients, listPayables } from '@/lib/revfinops-api'

interface RevFinOpsMetrics {
  totalClients: number
  pendingPayables: number
  activeAssignments: number
  totalRevenue: number
}

interface UseRevFinOpsMetricsReturn {
  metrics: RevFinOpsMetrics
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useRevFinOpsMetrics(): UseRevFinOpsMetricsReturn {
  const [metrics, setMetrics] = useState<RevFinOpsMetrics>({
    totalClients: 0,
    pendingPayables: 0,
    activeAssignments: 0,
    totalRevenue: 0
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadMetrics = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Fetch data from RevFinOps API
      const [clientsResult, payablesResult] = await Promise.all([
        listClients(),
        listPayables()
      ])

      // Handle errors from individual API calls
      if (!clientsResult.success && !payablesResult.success) {
        throw new Error('Erro ao carregar métricas')
      }

      // Calculate metrics from API responses
      const totalClients = clientsResult.success && clientsResult.data
        ? clientsResult.data.length
        : 0

      const pendingPayables = payablesResult.success && payablesResult.data
        ? payablesResult.data.filter(p => p.status === 'pending').length
        : 0

      // TODO: Implement active assignments count when assignment API is ready
      // For now, we'll use 0 as placeholder
      const activeAssignments = 0

      // TODO: Calculate total revenue from financial data
      // For now, we'll use 0 as placeholder
      const totalRevenue = 0

      setMetrics({
        totalClients,
        pendingPayables,
        activeAssignments,
        totalRevenue
      })
    } catch (err) {
      setError('Erro ao carregar métricas')
      console.error('Metrics error:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    loadMetrics()
  }, [loadMetrics])

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadMetrics()
    }, 30000)

    return () => clearInterval(interval)
  }, [loadMetrics])

  // Refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadMetrics()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [loadMetrics])

  return {
    metrics,
    isLoading,
    error,
    refresh: loadMetrics
  }
}
